import { Modal } from 'antd';
import classNames from 'classnames';
import React, { ComponentType, ForwardedRef, forwardRef } from 'react';
import ModalHeader from './modalHeader';
import { useWithModalLayout } from './useWithModalLayout';
import './styles.scss';

interface IPropsModalLayout {
  open: boolean;
  className?: string;
  onCancel: (param?: any) => any;
  loading?: boolean;
  title: string;
  destroyOnClose?: boolean;
  width?: number | string;
  [key: string]: any;
}

const VISUALLY_HIDDEN_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/**
 * Higher-order component mounting a component inside the shared modal shell. Returns a forwardRef wrapper that reads open, title and isMobile from useWithModalLayout, sizes the modal 328 on mobile or 500 otherwise unless width is given, disables the mask, keyboard and close affordances, and injects onCancel and setTitle into the wrapped component.
 *
 * @param ChildrenComponent - Component rendered as the modal body.
 * @returns The forwardRef wrapper rendering the modal.
 */
const withModalLayout = <P extends IPropsModalLayout>(ChildrenComponent: ComponentType<P>) => {
  const WithModalLayout: React.ForwardRefRenderFunction<
    unknown,
    React.PropsWithoutRef<P & { forwardedRef?: ForwardedRef<unknown> }>
  > = ({ forwardedRef, ...props }, ref) => {
    const { destroyOnClose = true } = props;

    const { isMobile, open, title, handleClose, setTitle } = useWithModalLayout(props);

    const RenderChildrenComponent = ChildrenComponent as any;

    return (
      <Modal
        open={open}
        destroyOnClose={destroyOnClose}
        width={props.width || (isMobile ? 328 : 500)}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        title={<span style={VISUALLY_HIDDEN_STYLE}>{title}</span>}
        styles={{ header: VISUALLY_HIDDEN_STYLE }}
        className={classNames(
          'modal-layout',
          props.loading && 'modal-layout-loading',
          props.className,
        )}
      >
        <ModalHeader className="custom-header-modal" title={title} onClick={() => handleClose()} />
        <div>
          <RenderChildrenComponent
            {...{ ...props, onCancel: handleClose, setTitle }}
            ref={forwardedRef || ref}
          />
        </div>
      </Modal>
    );
  };

  return forwardRef(WithModalLayout);
  // return WithModalLayout;
};

export default withModalLayout;
